// src/components/FilePreviewDialog/index.tsx
// Просмотрщик контекстных документов без скачивания.
// Тянет блоб с GET /projects/context-documents/{id}/download и рендерит по типу:
//   pdf → iframe, image → img, docx → mammoth(html), xlsx/csv → таблица (SheetJS),
//   txt/прочий текст → <pre>. Неподдержанное (.doc и т.п.) → кнопка «Скачать».
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Dialog, IconButton, Typography, CircularProgress, Tooltip } from '@mui/material';
import { Download, X, FileText, AlertCircle } from 'lucide-react';
import { fetchContextDocumentBlob } from 'services/filesApi';

interface FilePreviewDialogProps {
  open: boolean;
  documentId: string | null;
  fileName: string;
  /** MIME из листинга (file.mime_type), если известен — ускоряет выбор рендера */
  mimeType?: string | null;
  onClose: () => void;
}

type PreviewKind = 'pdf' | 'image' | 'docx' | 'sheet' | 'text' | 'unsupported';

function extOf(name: string): string {
  const m = name.toLowerCase().match(/\.([a-z0-9]+)$/);
  return m?.[1] ?? '';
}

function resolveKind(fileName: string, mimeType: string): PreviewKind {
  const ext = extOf(fileName);
  const mt = (mimeType || '').toLowerCase();

  if (mt.includes('pdf') || ext === 'pdf') return 'pdf';
  if (mt.startsWith('image/') || ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'bmp'].includes(ext))
    return 'image';
  if (mt.includes('wordprocessingml') || ext === 'docx') return 'docx';
  if (mt.includes('spreadsheetml') || mt.includes('ms-excel') || ext === 'xlsx' || ext === 'xls')
    return 'sheet';
  if (mt === 'text/csv' || ext === 'csv') return 'sheet';
  if (mt.startsWith('text/') || ['txt', 'md', 'json', 'log', 'xml', 'yaml', 'yml'].includes(ext))
    return 'text';
  return 'unsupported';
}

/** Лёгкая защита: вырезаем <script>, обработчики on*= и javascript: из HTML mammoth. */
function sanitizeHtml(html: string): string {
  return html
    .replace(/<script[\s\S]*?<\/script>/gi, '')
    .replace(/\son\w+\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi, '')
    .replace(/javascript:/gi, '');
}

interface SheetData {
  names: string[];
  /** имя листа → HTML таблицы */
  htmlByName: Record<string, string>;
}

interface RenderState {
  kind: PreviewKind;
  /** object URL для pdf/image */
  objectUrl?: string;
  /** для text */
  text?: string;
  /** для docx */
  html?: string;
  /** для sheet */
  sheet?: SheetData;
}

export default function FilePreviewDialog({
  open,
  documentId,
  fileName,
  mimeType,
  onClose,
}: FilePreviewDialogProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [render, setRender] = useState<RenderState | null>(null);
  const [activeSheet, setActiveSheet] = useState<string | null>(null);
  // Храним блоб, чтобы «Скачать» не делал повторный запрос.
  const blobRef = useRef<Blob | null>(null);
  const objectUrlRef = useRef<string | null>(null);

  const revokeUrl = useCallback(() => {
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!open || !documentId) return;
    let cancelled = false;

    setIsLoading(true);
    setError(null);
    setRender(null);
    setActiveSheet(null);
    blobRef.current = null;
    revokeUrl();

    (async () => {
      try {
        const { blob, mimeType: respMime } = await fetchContextDocumentBlob(documentId);
        if (cancelled) return;
        blobRef.current = blob;
        const kind = resolveKind(fileName, mimeType || respMime || '');

        if (kind === 'pdf' || kind === 'image') {
          const url = URL.createObjectURL(blob);
          objectUrlRef.current = url;
          setRender({ kind, objectUrl: url });
        } else if (kind === 'text') {
          const text = await blob.text();
          if (cancelled) return;
          setRender({ kind, text });
        } else if (kind === 'docx') {
          const arrayBuffer = await blob.arrayBuffer();
          if (cancelled) return;
          // Тяжёлый парсер грузим лениво, чтобы не раздувать основной бандл.
          const mammothMod = await import('mammoth');
          const mammoth = mammothMod.default ?? mammothMod;
          if (cancelled) return;
          const { value } = await mammoth.convertToHtml({ arrayBuffer });
          if (cancelled) return;
          setRender({ kind, html: sanitizeHtml(value) });
        } else if (kind === 'sheet') {
          const arrayBuffer = await blob.arrayBuffer();
          if (cancelled) return;
          const XLSX = await import('xlsx');
          if (cancelled) return;
          const wb = XLSX.read(arrayBuffer, { type: 'array' });
          const htmlByName: Record<string, string> = {};
          wb.SheetNames.forEach((n) => {
            htmlByName[n] = XLSX.utils.sheet_to_html(wb.Sheets[n], { editable: false });
          });
          setRender({ kind, sheet: { names: wb.SheetNames, htmlByName } });
          setActiveSheet(wb.SheetNames[0] ?? null);
        } else {
          setRender({ kind: 'unsupported' });
        }
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Не удалось открыть файл');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, documentId, fileName, mimeType, revokeUrl]);

  // Чистим object URL при закрытии / размонтировании.
  useEffect(() => {
    if (!open) revokeUrl();
    return () => revokeUrl();
  }, [open, revokeUrl]);

  const handleDownload = useCallback(() => {
    const blob = blobRef.current;
    if (!blob) return;
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName || 'document';
    a.click();
    URL.revokeObjectURL(url);
  }, [fileName]);

  const sheetTableHtml = useMemo(() => {
    if (render?.kind !== 'sheet' || !render.sheet || !activeSheet) return '';
    return render.sheet.htmlByName[activeSheet] ?? '';
  }, [render, activeSheet]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          height: '90vh',
          maxHeight: '90vh',
          borderRadius: 2,
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          px: 2,
          py: 1.5,
          borderBottom: '1px solid',
          borderColor: 'divider',
          flexShrink: 0,
        }}
      >
        <FileText size={16} style={{ flexShrink: 0, color: '#6b7280' }} />
        <Typography
          sx={{
            flex: 1,
            fontSize: 14,
            fontWeight: 600,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            color: 'text.primary',
          }}
        >
          {fileName}
        </Typography>
        <Tooltip title="Скачать">
          <span>
            <IconButton size="small" onClick={handleDownload} disabled={!blobRef.current}>
              <Download size={16} />
            </IconButton>
          </span>
        </Tooltip>
        <IconButton size="small" onClick={onClose} aria-label="Закрыть">
          <X size={16} />
        </IconButton>
      </Box>

      {/* Sheet tabs */}
      {render?.kind === 'sheet' && render.sheet && render.sheet.names.length > 1 && (
        <Box
          sx={{
            display: 'flex',
            gap: 0.5,
            px: 2,
            py: 1,
            borderBottom: '1px solid',
            borderColor: 'divider',
            overflowX: 'auto',
            flexShrink: 0,
          }}
        >
          {render.sheet.names.map((n) => (
            <Box
              key={n}
              component="button"
              type="button"
              onClick={() => setActiveSheet(n)}
              sx={{
                px: 1.25,
                py: 0.5,
                border: '1px solid',
                borderColor: n === activeSheet ? 'primary.main' : 'divider',
                bgcolor: n === activeSheet ? (t) => `${t.palette.primary.main}14` : 'transparent',
                color: n === activeSheet ? 'primary.main' : 'text.secondary',
                borderRadius: 1,
                fontSize: 12,
                fontWeight: 600,
                cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}
            >
              {n}
            </Box>
          ))}
        </Box>
      )}

      {/* Body */}
      <Box sx={{ flex: 1, minHeight: 0, position: 'relative', bgcolor: '#f8fafc' }}>
        {isLoading && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1.5,
              color: 'text.secondary',
            }}
          >
            <CircularProgress size={28} />
            <Typography sx={{ fontSize: 13 }}>Загружаем файл…</Typography>
          </Box>
        )}

        {!isLoading && error && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1.5,
              px: 3,
              textAlign: 'center',
            }}
          >
            <AlertCircle size={28} color="#dc2626" />
            <Typography sx={{ fontSize: 14, color: 'text.primary' }}>{error}</Typography>
            <Box
              component="button"
              type="button"
              onClick={handleDownload}
              disabled={!blobRef.current}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.75,
                px: 1.75,
                py: 0.75,
                border: 'none',
                borderRadius: 1.5,
                bgcolor: 'primary.main',
                color: '#fff',
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
                opacity: blobRef.current ? 1 : 0.5,
              }}
            >
              <Download size={14} /> Скачать
            </Box>
          </Box>
        )}

        {!isLoading && !error && render && (
          <Box sx={{ position: 'absolute', inset: 0, overflow: 'auto' }}>
            {render.kind === 'pdf' && (
              <iframe
                title={fileName}
                src={render.objectUrl}
                style={{ width: '100%', height: '100%', border: 'none' }}
              />
            )}

            {render.kind === 'image' && (
              <Box
                sx={{
                  minHeight: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  p: 2,
                }}
              >
                <img
                  src={render.objectUrl}
                  alt={fileName}
                  style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                />
              </Box>
            )}

            {render.kind === 'text' && (
              <Box
                component="pre"
                sx={{
                  m: 0,
                  p: 3,
                  fontSize: 13,
                  lineHeight: 1.6,
                  fontFamily:
                    'ui-monospace, SFMono-Regular, Menlo, Consolas, "Liberation Mono", monospace',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  color: 'text.primary',
                  bgcolor: '#fff',
                  minHeight: '100%',
                }}
              >
                {render.text}
              </Box>
            )}

            {render.kind === 'docx' && (
              <Box
                className="file-preview-docx"
                sx={{
                  p: { xs: 3, md: 5 },
                  maxWidth: 880,
                  mx: 'auto',
                  bgcolor: '#fff',
                  minHeight: '100%',
                  fontSize: 14.5,
                  lineHeight: 1.7,
                  color: 'text.primary',
                  '& img': { maxWidth: '100%', height: 'auto' },
                  '& table': { borderCollapse: 'collapse', width: '100%', my: 1.5 },
                  '& td, & th': { border: '1px solid #d1d5db', px: 1, py: 0.5 },
                  '& h1, & h2, & h3': { lineHeight: 1.3, mt: 2, mb: 1 },
                  '& p': { my: 1 },
                }}
                dangerouslySetInnerHTML={{ __html: render.html ?? '' }}
              />
            )}

            {render.kind === 'sheet' && (
              <Box
                className="file-preview-sheet"
                sx={{
                  p: 2,
                  bgcolor: '#fff',
                  minHeight: '100%',
                  '& table': { borderCollapse: 'collapse', fontSize: 13 },
                  '& td, & th': {
                    border: '1px solid #d1d5db',
                    px: 1,
                    py: 0.5,
                    whiteSpace: 'nowrap',
                  },
                  '& tr:first-of-type td': { fontWeight: 600, bgcolor: '#f1f5f9' },
                }}
                dangerouslySetInnerHTML={{ __html: sheetTableHtml }}
              />
            )}

            {render.kind === 'unsupported' && (
              <Box
                sx={{
                  minHeight: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1.5,
                  px: 3,
                  textAlign: 'center',
                }}
              >
                <FileText size={32} color="#9ca3af" />
                <Typography sx={{ fontSize: 14, color: 'text.primary' }}>
                  Предпросмотр для этого формата недоступен
                </Typography>
                <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>
                  Скачайте файл, чтобы открыть его локально
                </Typography>
                <Box
                  component="button"
                  type="button"
                  onClick={handleDownload}
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.75,
                    mt: 0.5,
                    px: 1.75,
                    py: 0.75,
                    border: 'none',
                    borderRadius: 1.5,
                    bgcolor: 'primary.main',
                    color: '#fff',
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  <Download size={14} /> Скачать
                </Box>
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Dialog>
  );
}
